export const getValue = (deal: any, fieldName: string | string[]): any => {
  if (Array.isArray(fieldName)) {
    for (const name of fieldName) {
      const value = deal[name];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return 'N/A';
  }
  return deal[fieldName] ?? 'N/A';
};

export const formatPercentage = (value: string | number): string => {
  if (!value || value === 'N/A') return 'N/A';
  return `${value}%`;
};

export const formatDate = (value: string): string => {
  if (!value || value === 'N/A') return 'N/A';
  return new Date(value).toLocaleDateString();
};

export const formatCurrency = (value: any): string => {
  if (!value || value === 'N/A') return 'N/A';
  return `$${Number(value).toLocaleString()}`;
};

const formatBoolean = (value: boolean | string) => {
  if (!value || value === 'N/A') return 'No';
  return value ? 'Yes' : 'No';
};

export const formatPipelineType = (value: string): string => {
  if (!value || value === 'N/A') return value;
  return value
    .replace(/_/g, '/')
    .split('/')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('/');
};

export type DealType =
  | 'COMMERCIAL_ENDORSEMENT'
  | 'EVENT_APPEARANCE'
  | 'EVENT_OWNED'
  | 'CONTENT_PRODUCTION'
  | 'LICENSING_IP'
  | 'EQUITY_INVESTMENT'
  | 'BRANDED_ENT';

export const mapDealToDetailFields = (deal: any, dealType: DealType) => {
  const commonOpportunityFieldsFirst = [
    {
      label: 'Opportunity name',
      value: getValue(deal, ['name', 'title']),
      fieldName: 'name',
    },
    {
      label: 'Client name',
      value: getValue(deal, ['clientName', 'client']),
      fieldName: 'clientName',
      isAvatar: true,
    },
    {
      label: 'Opportunity owner',
      value: getValue(deal, ['ownerName', 'owner']),
      fieldName: 'ownerName',
      isAvatar: true,
      isSelect: true,
    },
    {
      label: 'Pipeline type',
      value: getValue(deal, ['pipelineType', 'dealType']),
      fieldName: 'pipelineType',
      format: formatPipelineType,
    },
    {
      label: 'Probability (%)',
      value: getValue(deal, 'probability'),
      fieldName: 'probability',
      format: formatPercentage,
    },
    {
      label: 'Stage',
      value: getValue(deal, ['stageName', 'stage']),
      fieldName: 'stageName',
      isSelect: true,
    },
  ];

  const commonOpportunityFieldsSecond = [
    {
      label: 'Revenue tier',
      value: getValue(deal, 'revenueTier'),
      fieldName: 'revenueTier',
    },
    {
      label: 'Ceiling tier',
      value: getValue(deal, 'ceilingTier'),
      fieldName: 'ceilingTier',
    },
    {
      label: 'Client orientation',
      value: getValue(deal, 'clientOrientation'),
      fieldName: 'clientOrientation',
    },
    {
      label: 'Career stage',
      value: getValue(deal, ['careerState', 'careerStage']),
      fieldName: 'careerState',
    },
  ];

  const commonFinancialFields = [
    {
      label: 'Deal value (gross)',
      value: getValue(deal, ['dealValue', 'amount', 'value']),
      fieldName: 'dealValue',
      format: (v: any) => (v === 'N/A' ? 'N/A' : formatCurrency(v)),
    },
    {
      label: 'Commission rate (%)',
      value: getValue(deal, 'commissionRate'),
      fieldName: 'commissionRate',
      format: formatPercentage,
    },
    {
      label: 'Commission value',
      value: getValue(deal, 'commissionValue'),
      fieldName: 'commissionValue',
      format: (v: any) => (v === 'N/A' ? 'N/A' : formatCurrency(v)),
    },
    {
      label: 'Deal structure',
      value: getValue(deal, 'dealStructure'),
      fieldName: 'dealStructure',
    },
    {
      label: 'Equity/ownership (%)',
      value: getValue(deal, 'equity'),
      fieldName: 'equity',
      format: formatPercentage,
    },
    { label: 'Backend', value: getValue(deal, 'backend'), fieldName: 'backend' },
    {
      label: 'Expected close date',
      value: getValue(deal, 'closeDate'),
      fieldName: 'closeDate',
      format: formatDate,
    },
  ];

  const commonAttributionFields = [
    {
      label: 'Leadership sponsor',
      value: getValue(deal, 'leadershipSponsor'),
      fieldName: 'leadershipSponsor',
    },
    { label: 'Source', value: getValue(deal, 'source'), fieldName: 'source' },
    {
      label: 'Partner account',
      value: getValue(deal, 'partnerAccount'),
      fieldName: 'partnerAccount',
    },
    {
      label: 'Partner contact',
      value: getValue(deal, 'partnerContact'),
      fieldName: 'partnerContact',
    },
    {
      label: 'Days in current stage',
      value: getValue(deal, 'daysInCurrentStage'),
      fieldName: 'daysInCurrentStage',
    },
  ];

  const commonAdditionalFields = [
    { label: 'Outcome', value: getValue(deal, 'outcome'), fieldName: 'outcome' },
    {
      label: 'Loss reason',
      value: getValue(deal, 'lossReason'),
      fieldName: 'lossReason',
    },
    {
      label: 'Pass reason',
      value: getValue(deal, 'passReason'),
      fieldName: 'passReason',
    },
    { label: 'Competitor', value: getValue(deal, 'competitor'), fieldName: 'competitor' },
  ];

  const commonSystemInformationFields = [
    {
      label: 'Created by',
      value: getValue(deal, ['createdBy', 'createdByName']),
      isAvatar: true,
    },
    {
      label: 'Created date',
      value: getValue(deal, ['createdDate', 'createdAt']),
      format: formatDate,
    },
    {
      label: 'Last modified date',
      value: getValue(deal, ['lastModifiedDate', 'updatedAt']),
      format: formatDate,
      // hasActivity: true,
    },
  ];

  const financialInformationDualRevenueFields = [
    {
      label: 'Client talent fee ($)',
      value: getValue(deal, 'clientTalentFee'),
      format: formatCurrency,
    },
    {
      label: 'Talent fee commission rate (%)',
      value: getValue(deal, 'talentFeeCommissionRate'),
      fieldName: 'talentFeeCommissionRate',
      format: formatPercentage,
    },
    {
      label: 'Talent fee commission',
      value: getValue(deal, 'talentFeeCommission'),
      fieldName: 'talentFeeCommission',
    },
    {
      label: '3AS production fee ($)',
      value: getValue(deal, '3ASProductionFee'),
      fieldName: '3ASProductionFee',
      format: formatCurrency,
    },
    {
      label: 'Total 3AS revenue',
      value: getValue(deal, 'total3ASRevenue'),
      fieldName: 'total3ASRevenue',
    },
    {
      label: 'Owned revenue (%)',
      value: getValue(deal, 'ownedRevenue'),
      fieldName: 'ownedRevenue',
      format: formatPercentage,
    },
  ];

  const projectInformationFields = [
    {
      label: 'Project type',
      value: getValue(deal, 'projectType'),
      fieldName: 'projectType',
    },
    {
      label: 'Entertainment crossover',
      value: getValue(deal, 'entertainmentCrossover'),
      fieldName: 'entertainmentCrossover',
    },
    {
      label: 'Career Stage Check',
      value: getValue(deal, 'careerStageCheck'),
      fieldName: 'careerStageCheck',
    },
    {
      label: 'LG/3AMs involved',
      value: getValue(deal, 'lg3AMsInvolved'),
      fieldName: 'lg3AMsInvolved',
      format: formatBoolean,
    },
    {
      label: 'Client role',
      value: getValue(deal, 'clientRole'),
      fieldName: 'clientRole',
    },
    {
      label: 'Client ownership (%)',
      value: getValue(deal, 'clientOwnership'),
      fieldName: 'clientOwnership',
      format: formatPercentage,
    },
    {
      label: '3AS ownership (%)',
      value: getValue(deal, '3ASOwnership'),
      fieldName: '3ASOwnership',
      format: formatPercentage,
    },
    {
      label: 'Buyer/Platform',
      value: getValue(deal, 'buyerPlatform'),
      fieldName: 'buyerPlatform',
    },
  ];

  let typeSpecificFields: any = [];

  switch (dealType) {
    case 'COMMERCIAL_ENDORSEMENT':
      typeSpecificFields = [
        { label: 'Category', value: getValue(deal, 'category'), fieldName: 'category' },
        { label: 'Deal type', value: getValue(deal, 'dealType'), fieldName: 'dealType' },
        {
          label: 'Contract term (month)',
          value: getValue(deal, 'contractTerm'),
          fieldName: 'contractTerm',
          format: (v: any) => (v === 'N/A' ? 'N/A' : `${v} months`),
        },
        {
          label: 'Exclusivity scope',
          value: getValue(deal, 'exclusivityScope'),
          fieldName: 'exclusivityScope',
        },
        {
          label: 'Client fit score',
          value: getValue(deal, 'clientFitScore'),
          fieldName: 'clientFitScore',
        },
        {
          label: 'Client approval',
          value: getValue(deal, 'clientApproval'),
          fieldName: 'clientApproval',
        },
        {
          label: 'Renewal probability (%)',
          value: getValue(deal, 'renewalProbability'),
          fieldName: 'renewalProbability',
        },
      ];
      break;

    case 'EVENT_APPEARANCE':
      typeSpecificFields = [
        {
          label: 'Event type',
          value: getValue(deal, 'eventType'),
          fieldName: 'eventType',
        },
        {
          label: 'Event name',
          value: getValue(deal, 'eventName'),
          fieldName: 'eventName',
        },
        {
          label: 'Event date',
          value: getValue(deal, 'eventDate'),
          fieldName: 'eventDate',
          format: formatDate,
        },
        {
          label: 'Event location',
          value: getValue(deal, 'eventLocation'),
          fieldName: 'eventLocation',
        },
        {
          label: 'Duration (hours)',
          value: getValue(deal, 'duration'),
          fieldName: 'duration',
        },
        {
          label: 'Travel required',
          value: formatBoolean(deal.travelRequired),
          fieldName: 'travelRequired',
        },
        {
          label: 'Content rights',
          value: getValue(deal, 'contentRights'),
          fieldName: 'contentRights',
        },
        {
          label: 'Payment terms',
          value: getValue(deal, 'paymentTerms'),
          fieldName: 'paymentTerms',
        },
      ];
      break;

    case 'EVENT_OWNED':
      typeSpecificFields = [
        {
          label: 'Event property name',
          value: getValue(deal, 'eventPropertyName'),
          fieldName: 'eventPropertyName',
        },
        {
          label: 'Ownership structure',
          value: getValue(deal, 'ownershipStructure'),
          fieldName: 'ownershipStructure',
        },
        {
          label: '3AS Ownership (%)',
          value: getValue(deal, '3ASOwnership'),
          fieldName: '3ASOwnership',
          format: formatPercentage,
        },
        {
          label: 'Expected attendance',
          value: getValue(deal, 'expectedAttendance'),
          fieldName: 'expectedAttendance',
        },
        {
          label: 'Ticket revenue target ($)',
          value: getValue(deal, 'ticketRevenueTarget'),
          fieldName: 'ticketRevenueTarget',
          format: formatCurrency,
        },
        {
          label: 'Sponsorship revenue target ($)',
          value: getValue(deal, 'sponsorshipRevenueTarget'),
          fieldName: 'sponsorshipRevenueTarget',
          format: formatCurrency,
        },
        {
          label: 'Production budget ($)',
          value: getValue(deal, 'productionBudget'),
          fieldName: 'productionBudget',
          format: formatCurrency,
        },
        {
          label: 'Projected margin (%)',
          value: getValue(deal, 'projectedMargin'),
          fieldName: 'projectedMargin',
          format: formatPercentage,
        },
      ];
      break;

    case 'CONTENT_PRODUCTION':
      typeSpecificFields = [];
      break;

    case 'LICENSING_IP':
      typeSpecificFields = [
        {
          label: 'License type',
          value: getValue(deal, 'licenseType'),
          fieldName: 'licenseType',
        },
        {
          label: 'AI rights flag',
          value: getValue(deal, 'AIRightsFlag'),
          fieldName: 'AIRightsFlag',
          format: formatBoolean,
        },
        {
          label: 'Guaranteed minimum ($)',
          value: getValue(deal, 'guaranteedMinimum'),
          fieldName: 'guaranteedMinimum',
          format: formatCurrency,
        },
        {
          label: 'Royalty rate (%)',
          value: getValue(deal, 'royaltyRate'),
          fieldName: 'royaltyRate',
          format: formatPercentage,
        },
        {
          label: 'Territory',
          value: getValue(deal, 'territory'),
          fieldName: 'territory',
        },
        {
          label: 'Product approval',
          value: getValue(deal, 'productApproval'),
          fieldName: 'productApproval',
        },
        {
          label: 'Reporting frequency',
          value: getValue(deal, 'reportingFrequency'),
          fieldName: 'reportingFrequency',
        },
      ];
      break;

    case 'EQUITY_INVESTMENT':
      typeSpecificFields = [
        {
          label: 'Investment type',
          value: getValue(deal, 'investmentType'),
          fieldName: 'investmentType',
        },
        {
          label: 'Entity name',
          value: getValue(deal, 'entityName'),
          fieldName: 'entityName',
        },
        {
          label: 'Industry/Sector',
          value: getValue(deal, 'industrySector'),
          fieldName: 'industrySector',
        },
        {
          label: 'Investment amount ($)',
          value: getValue(deal, 'investmentAmount'),
          fieldName: 'investmentAmount',
          format: formatCurrency,
        },
        {
          label: 'Ownership target (%)',
          value: getValue(deal, 'ownershipTarget'),
          fieldName: 'ownershipTarget',
          format: formatPercentage,
        },
        {
          label: 'Pre-Money valuation ($)',
          value: getValue(deal, 'preMoneyValuation'),
          fieldName: 'preMoneyValuation',
          format: formatCurrency,
        },
        {
          label: 'Board seat',
          value: getValue(deal, 'boardSeat'),
          fieldName: 'boardSeat',
        },
        {
          label: 'Target exit multiple',
          value: getValue(deal, 'targetExitMultiple'),
          fieldName: 'targetExitMultiple',
          format: (v: any) => (v === 'N/A' ? 'N/A' : `${v}x`),
        },
      ];
      break;

    case 'BRANDED_ENT':
      typeSpecificFields = [
        {
          label: 'Buyer type',
          value: getValue(deal, 'buyerType'),
          fieldName: 'buyerType',
        },
        {
          label: 'Organization name',
          value: getValue(deal, 'organizationName'),
          fieldName: 'organizationName',
        },
        {
          label: 'Qualification score',
          value: getValue(deal, 'qualificationScore'),
          fieldName: 'qualificationScore',
        },
        {
          label: 'Client placement',
          value: getValue(deal, 'clientPlacement'),
          fieldName: 'clientPlacement',
        },
        {
          label: 'Placed clients',
          value: getValue(deal, 'placedClients'),
          fieldName: 'placedClients',
        },
        {
          label: 'Engagement type',
          value: getValue(deal, 'engagementType'),
          fieldName: 'engagementType',
        },
        {
          label: 'Development fee ($)',
          value: getValue(deal, 'developmentFee'),
          fieldName: 'developmentFee',
          format: formatCurrency,
        },
        {
          label: 'Retainer value ($)',
          value: getValue(deal, 'retainerValue'),
          fieldName: 'retainerValue',
          format: formatCurrency,
        },
        {
          label: 'IP ownership (%)',
          value: getValue(deal, 'IPOwnership'),
          fieldName: 'IPOwnership',
          format: formatPercentage,
        },
        {
          label: 'Lionsgate involved',
          value: getValue(deal, 'lionsgateInvolved'),
          fieldName: 'lionsgateInvolved',
          format: formatBoolean,
        },
        {
          label: 'Recruiting pipeline',
          value: getValue(deal, 'recruitingPipeline'),
          fieldName: 'recruitingPipeline',
          format: formatBoolean,
        },
      ];
      break;
  }

  return {
    opportunityInfo: [
      ...commonOpportunityFieldsFirst,
      ...typeSpecificFields,
      ...commonOpportunityFieldsSecond,
    ],
    financialInfo: commonFinancialFields,
    attributionTracking: commonAttributionFields,
    additionalInfo: commonAdditionalFields,
    systemInfo: commonSystemInformationFields,
    projectInformationFields,
    financialInformationDualRevenueFields,
    typeSpecificFields,
  };
};
